// Auth state for the debug dashboard. Mirrors the mobile useAuthStore shape but
// is web-only (tokens in localStorage). The app gates on `status`:
//   'unknown'       — bootstrapping (token present, /me in flight)
//   'authenticated' — logged in, `user` populated
//   'anonymous'     — no valid session; show the login screen
import { create } from 'zustand';
import { getMe, login as apiLogin, logout as apiLogout, type AuthUser } from 'api/authApi';
import { getAuthToken } from 'auth/tokenStorage';

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

interface AuthStore {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;

  /** Resolve the initial session on app mount (validates any stored token). */
  bootstrap: () => Promise<void>;
  /** Log in with credentials and load the user. Throws on failure (caller shows the error). */
  signIn: (username: string, password: string) => Promise<void>;
  /** Log out and reset to anonymous. */
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()((set) => ({
  status: 'unknown',
  user: null,
  error: null,

  bootstrap: async () => {
    if (!getAuthToken()) {
      set({ status: 'anonymous', user: null });
      return;
    }
    try {
      const user = await getMe();
      set({ status: 'authenticated', user, error: null });
    } catch {
      // Token invalid/expired and refresh (handled in restClient) couldn't
      // recover — treat as logged out.
      set({ status: 'anonymous', user: null });
    }
  },

  signIn: async (username, password) => {
    set({ error: null });
    await apiLogin(username, password);
    const user = await getMe();
    set({ status: 'authenticated', user, error: null });
  },

  signOut: async () => {
    await apiLogout();
    set({ status: 'anonymous', user: null, error: null });
  },
}));
